import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";
import JobContainersModal from "../components/JobContainersModal";
import JobDocumentModal from "../components/JobDocumentModal";
import JobFormModal from "../components/JobFormModal";
/* ─────────────────────────────────────────────────────────────────
  Package Unit helpers (multi package-type support)
───────────────────────────────────────────────────────────────── */
const PACKAGE_UNIT_OPTIONS = [
  { value: "", label: "-Select-" },
  { value: "BDL", label: "BUNDLE (BDL)" },
  { value: "CAS", label: "CASES (CAS)" },
  { value: "SHT", label: "SHEETS (SHT)" },
  { value: "CHT", label: "CHEST (CHT)" },
  { value: "CLS", label: "COILS (CLS)" },
  { value: "COL", label: "COLLIES (COL)" },
  { value: "CON", label: "CONTAINER (CON)" },
  { value: "CRT", label: "CRATES (CRT)" },
  { value: "CSK", label: "WOODEN CASKS (CSK)" },
  { value: "CTN", label: "CARTONS (CTN)" },
  { value: "CYL", label: "CYLINDER (CYL)" },
  { value: "DRM", label: "DRUMS (DRM)" },
  { value: "ENV", label: "STEEL ENVELOPES (ENV)" },
  { value: "FLK", label: "FLASK (FLK)" },
  { value: "FUT", label: "FUTS (FUT)" },
  { value: "HBK", label: "HABBUCK (HBK)" },
  { value: "JBL", label: "JUMBLE BALE (JBL)" },
  { value: "JTA", label: "JOTTA (JTA)" },
  { value: "KEG", label: "KEGGS (KEG)" },
  { value: "LFT", label: "LIFT (LFT)" },
  { value: "LOG", label: "LOGS (LOG)" },
  { value: "NGT", label: "INGOT (NGT)" },
  { value: "PAL", label: "PALLS (PAL)" },
  { value: "PKG", label: "PACKAGES (PKG)" },
  { value: "PLT", label: "PALLETS (PLT)" },
  { value: "QDS", label: "QUADS (QDS)" },
  { value: "REL", label: "REELS (REL)" },
  { value: "RLS", label: "ROOLS (RLS)" },
  { value: "SKD", label: "SKID & SKIDDED PKGS (SKD)" },
  { value: "SLB", label: "SLABS (SLB)" },
  { value: "TBL", label: "TABLE (TBL)" },
  { value: "TIN", label: "TINS (TIN)" },
  { value: "TRK", label: "TRUNK (TRK)" },
  { value: "UNT", label: "UNITS (UNT)" },
  { value: "PCS", label: "PIECES (PCS)" },
  { value: "BAL", label: "BALES" },

];

const emptyPackageLine = () => ({
  _key: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  no_of_packages: "",
  package_unit: "",
});

/** Human-readable "20 Bales, 15 Bags" summary from a packages array. */
const summarizePackages = (packages) => {
  if (!Array.isArray(packages) || packages.length === 0) return "—";
  return (
    packages
      .filter((p) => p.no_of_packages || p.package_unit)
      .map((p) => `${p.no_of_packages || 0} ${p.package_unit || ""}`.trim())
      .join(", ") || "—"
  );
};

const PackageLinesEditor = ({ lines, onChange }) => {
  const updateLine = (key, field, value) => {
    onChange(lines.map((l) => (l._key === key ? { ...l, [field]: value } : l)));
  };

  const addLine = () => onChange([...lines, emptyPackageLine()]);

  const removeLine = (key) => {
    if (lines.length === 1) {
      // keep at least one row, just clear it
      onChange([emptyPackageLine()]);
      return;
    }
    onChange(lines.filter((l) => l._key !== key));
  };

  const total = lines.reduce((sum, l) => sum + (parseFloat(l.no_of_packages) || 0), 0);

  return (
    <div className="col-12 mt-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-semibold mb-0" style={{ fontSize: 13 }}>
          Packages <span className="text-muted fw-normal">(add a line per package type)</span>
        </label>
        <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
          Total: {total || 0}
        </span>
      </div>

      <div className="d-flex flex-column gap-2">
        {lines.map((line, idx) => (
          <div className="row g-2 align-items-center" key={line._key}>
            <div className="col-4 col-md-3">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="No. of Packages"
                value={line.no_of_packages}
                onChange={(e) => updateLine(line._key, "no_of_packages", e.target.value)}
              />
            </div>
            <div className="col-5 col-md-4">
              <select
                className="form-select form-select-sm"
                value={line.package_unit}
                onChange={(e) => updateLine(line._key, "package_unit", e.target.value)}
              >
                {PACKAGE_UNIT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                title="Remove line"
                onClick={() => removeLine(line._key)}
              >
                <i className="ri-close-line" />
              </button>
            </div>
            {idx === lines.length - 1 && (
              <div className="col-auto">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLine} title="Add another package type">
                  <i className="ri-add-line me-1" />
                  Add
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


const MasterFormModal = ({ show, title, formFields, idField, endpoint, hasPhoto, onClose, onSaved }) => {
  const initialFormState = useMemo(
    () => formFields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), { [idField]: 0 }),
    [formFields, idField]
  );

  const [formData, setFormData] = useState(initialFormState);
  const [photo, setPhoto] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState("");
  const [removePhoto, setRemovePhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setFormData(initialFormState);
      setPhoto(null);
      setExistingPhoto("");
      setRemovePhoto(false);
    }
  }, [show, initialFormState]);

  const showAlert = (icon, alertTitle, text, timer = 2000) => {
    Swal.fire({ icon, title: alertTitle, text: text || "Something went wrong.", timer, showConfirmButton: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      showAlert("warning", "Invalid File", "Only JPEG, PNG, WEBP, or GIF allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showAlert("warning", "File Too Large", "Photo must be under 5MB.");
      return;
    }
    setPhoto(file);
    setRemovePhoto(false);
    e.target.value = "";
  };

  const handleRemovePhoto = () => {
    if (existingPhoto) setRemovePhoto(true);
    setPhoto(null);
    setExistingPhoto("");
  };

  const previewSrc = photo ? URL.createObjectURL(photo) : existingPhoto || null;

  const handleClose = () => {
    setFormData(initialFormState);
    setPhoto(null);
    setExistingPhoto("");
    setRemovePhoto(false);
    onClose();
  };

  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      let payload = formData;
      let options;

      if (hasPhoto) {
        payload = new FormData();
        Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
        if (photo) {
          payload.append("photo", photo);
          payload.append("existing_photo", existingPhoto);
        }
        if (removePhoto) {
          payload.append("remove_photo", "true");
          payload.append("existing_photo", existingPhoto);
        }
        options = { headers: { "Content-Type": "multipart/form-data" } };
      }

      const response = await post(endpoint, payload, options);

      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        const savedData = formData;
        handleClose();
        onSaved(savedData);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  const PhotoUploadSection = hasPhoto ? (
    <div className="mt-3">
      <label className="form-label fw-semibold">{title} Photo</label>

      {previewSrc ? (
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative" style={{ width: 100, height: 100 }}>
            <img
              src={previewSrc}
              alt="preview"
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: "1px solid #dee2e6" }}
            />
            <button
              type="button"
              className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
              style={{ width: 22, height: 22, fontSize: 12, borderRadius: "50%", transform: "translate(40%, -40%)" }}
              onClick={handleRemovePhoto}
              title="Remove photo"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div>
            <p className="mb-1 small text-muted">{photo ? photo.name : "Current photo"}</p>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-refresh-line me-1" />
              Change Photo
            </button>
          </div>
        </div>
      ) : (
        <div
          className="border rounded p-4 text-center"
          style={{ borderStyle: "dashed", borderColor: "#6c63ff", cursor: "pointer", background: "#f8f7ff" }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handlePhotoSelect({ target: { files: [file], value: "" } });
          }}
        >
          <i className="ri-image-add-line" style={{ fontSize: 32, color: "#6c63ff" }} />
          <p className="mb-0 text-muted small mt-2">
            Click or drag & drop a photo here
            <br />
            <span style={{ fontSize: 11 }}>JPEG, PNG, WEBP · max 5MB</span>
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: "none" }}
        onChange={handlePhotoSelect}
      />
    </div>
  ) : null;

  return (
    <CommonModal
      show={show}
      title={title}
      formFields={formFields}
      formData={formData}
      onChange={handleChange}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitText="Save"
      extraContent={PhotoUploadSection}
    />
  );
};

const All_jobs = () => {
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [shipperOptions, setShipperOptions] = useState([]);
  const [linerOptions, setLinerOptions] = useState([]);
  const [CSFLocationOptions, setCFSLocationOptions] = useState([]);
  const [bondOptions, setBondOptions] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [indentingOptions, setIndentingOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  /* document modal state */
  const [docModal, setDocModal] = useState(false);
  const [docJob, setDocJob] = useState(null);

  /* multiple package-type rows (Qty + Unit), e.g. 50 Bales + 30 Cartons */
  const [packageLines, setPackageLines] = useState([emptyPackageLine()]);

  const allFormFields = useMemo(() => [
    { type: "section", label: "Job Information" },
    { name: "job_date", label: "Job Date", type: "date", required: true, col: 3 },
    {
      name: "job_type", label: "Job Type", type: "select", required: true, col: 3,
      options: [
        { value: "", label: "-select-" },
        { value: "import", label: "Import" },
        { value: "export", label: "Export" },
        { value: "transship", label: "Transship" },
      ],
    },
    {
      name: "status", label: "Status", type: "select", required: true, col: 3,
      options: [
        { value: 0, label: "-select-" },
        { value: 1, label: "Yet to Arrive" },
        { value: 2, label: "BE Filed" },
        { value: 3, label: "Arrived" },
        { value: 4, label: "Duty Payment" },
        { value: 5, label: "Examination" },
        { value: 6, label: "OOC" },
        { value: 7, label: "Ready to Despatch" },
        { value: 9, label: "Billing" },
        { value: 8, label: "Lot Completed" },
      ],
    },
    { name: "lot_no", label: "Lot No.", type: "text", col: 3 },

    { type: "section", label: "Party Details" },
    { name: "consignee_id_fk", label: "Consignee", type: "select", required: true, col: 3, options: consigneeOptions },
    { name: "shipper_id_fk", label: "Shipper", type: "select", col: 3, options: shipperOptions },
    { name: "liner_id_fk", label: "Liner / Shipping Line", type: "select", col: 3, options: linerOptions },
    { name: "indenting_agent_id_fk", label: "Indenting Agent Name", type: "select", col: 3, options: indentingOptions },

    { type: "section", label: "Bill of Lading" },
    { name: "bl_number", label: "BL Number", type: "text", required: true, col: 3 },
    {
      name: "bl_type", label: "BL Type", type: "select", col: 3,
      options: [
        { value: "original", label: "Original" },
        { value: "telex_release", label: "Telex Release" },
        { value: "sea_waybill", label: "Sea Waybill" },
        { value: "express_bl", label: "Express BL" },
      ],
    },
    { name: "bl_date", label: "BL Date", type: "date", col: 3 },
    { name: "invoice_no", label: "Invoice No.", type: "text", col: 3 },
    { name: "invoice_date", label: "Invoice Date", type: "date", col: 3 },
    { name: "invoice_value", label: "Invoice Value", type: "number", col: 3 },
    { name: "currency_id_fk", label: "Currency", type: "select", col: 3, options: currencyOptions },
    { name: "do", label: "Delivery Order", type: "checkbox", col: 3 },
    { name: "hss", label: "High Sea Sale", type: "checkbox", col: 3 },
    { name: "hss_party", label: "Hss Party Name", type: "text", col: 3 },
    { name: "hss_invoice_no", label: "HSS Invoice No", type: "text", col: 3 },
    { name: "hss_invoice_date", label: "HSS Invoice Date", type: "date", col: 3 },

    { type: "section", label: "Vessel & Ports" },
    { name: "vessel_name", label: "Vessel Name", type: "text", col: 3 },
    { name: "voyage_no", label: "Voyage No.", type: "text", col: 3 },
    { name: "pol", label: "Port of Loading", type: "text", col: 3 },
    { name: "pod", label: "Port of Discharge", type: "text", col: 3 },

    { type: "section", label: "Arrival & Free Days" },
    { name: "ts_arrival", label: "T/S Arrival Date", type: "date", col: 3 },
    { name: "pod_arrival", label: "POD Arrival Date", type: "date", col: 3 },
    { name: "cfs_id_fk", label: "CFS", type: "select", col: 3, options: CSFLocationOptions },
    { name: "cfs_nomination", label: "CFS Nomination Date", type: "date", col: 3 },
    { name: "free_days", label: "CFS Free Days", type: "number", col: 3 },
    { name: "free_date", label: "CFS Free Date", type: "date", col: 3, readOnly: true },
    { name: "dem_upto", label: "Detention Free Days", type: "number", col: 3 },
    { name: "detention_upto", label: "Detention Free Date", type: "date", col: 3, readOnly: true },
    { name: "demurrage_free_days", label: "Demurrage Free Days", type: "number", col: 3 },
    { name: "demurrage_free_date", label: "Demurrage Free Date", type: "date", col: 3, readOnly: true },

    { type: "section", label: "Bill of Entry" },
    { name: "be_number", label: "BE Number", type: "text", col: 6 },
    { name: "be_date", label: "BE Date", type: "date", col: 6 },

    { type: "section", label: "Cargo Details" },
    { name: "cargo_desc", label: "Cargo Description", type: "select", col: 12, options: commodityOptions },
    { name: "hs_code", label: "HS Code", type: "text", col: 2 },
    { name: "origin", label: "Country of Origin", type: "text", col: 2 },
    {
      name: "container_type", label: "Container Type", type: "select", col: 2,
      options: [{ value: "FCL", label: "FCL" }, { value: "LCL", label: "LCL" }]
    },
    { name: "no_of_containers", label: "No. of Containers", type: "number", col: 2 },
    { name: "gross_weight", label: "Gross Weight", type: "number", col: 3 },
    { name: "net_weight", label: "Net Weight", type: "number", col: 2 },
    {
      name: "weight_unit", label: "Weight Unit", type: "select", col: 3,
      options: [{ value: "mt", label: "MT" }, { value: "kg", label: "KG" }]
    },
    { name: "packages", label: "No. of Packages", type: "number", col: 2 },
    {
      name: "package_unit", label: "Package Unit", type: "select", col: 2,
      options: [
        { value: "", label: "-Select-" },
        { value: "BDL", label: "BUNDLE (BDL)" },
        { value: "CAS", label: "CASES (CAS)" },
        { value: "SHT", label: "SHEETS (SHT)" },
        { value: "CHT", label: "CHEST (CHT)" },
        { value: "CLS", label: "COILS (CLS)" },
        { value: "COL", label: "COLLIES (COL)" },
        { value: "CON", label: "CONTAINER (CON)" },
        { value: "CRT", label: "CRATES (CRT)" },
        { value: "CSK", label: "WOODEN CASKS (CSK)" },
        { value: "CTN", label: "CARTONS (CTN)" },
        { value: "CYL", label: "CYLINDER (CYL)" },
        { value: "DRM", label: "DRUMS (DRM)" },
        { value: "ENV", label: "STEEL ENVELOPES (ENV)" },
        { value: "FLK", label: "FLASK (FLK)" },
        { value: "FUT", label: "FUTS (FUT)" },
        { value: "HBK", label: "HABBUCK (HBK)" },
        { value: "JBL", label: "JUMBLE BALE (JBL)" },
        { value: "JTA", label: "JOTTA (JTA)" },
        { value: "KEG", label: "KEGGS (KEG)" },
        { value: "LFT", label: "LIFT (LFT)" },
        { value: "LOG", label: "LOGS (LOG)" },
        { value: "NGT", label: "INGOT (NGT)" },
        { value: "PAL", label: "PALLS (PAL)" },
        { value: "PKG", label: "PACKAGES (PKG)" },
        { value: "PLT", label: "PALLETS (PLT)" },
        { value: "QDS", label: "QUADS (QDS)" },
        { value: "REL", label: "REELS (REL)" },
        { value: "RLS", label: "ROOLS (RLS)" },
        { value: "SKD", label: "SKID & SKIDDED PKGS (SKD)" },
        { value: "SLB", label: "SLABS (SLB)" },
        { value: "TBL", label: "TABLE (TBL)" },
        { value: "TIN", label: "TINS (TIN)" },
        { value: "TRK", label: "TRUNK (TRK)" },
        { value: "UNT", label: "UNITS (UNT)" },
        { value: "PCS", label: "PIECES (PCS)" },
        { value: "BAL", label: "BALES" },
      ],
    },
    { type: "section", label: "Documents Received" },
    {
      name: "copy_doc", label: "Copy Document", type: "select", col: 3,
      options: [{ value: "YES", label: "YES" }, { value: "NO", label: "NO" }]
    },
    { name: "copy_doc_date", label: "Copy Doc Date", type: "date", col: 3 },
    {
      name: "orig_doc", label: "Original Document", type: "select", col: 3,
      options: [{ value: "YES", label: "YES" }, { value: "NO", label: "NO" }]
    },
    { name: "orig_doc_date", label: "Original Doc Date", type: "date", col: 3 },

    { type: "section", label: "License & Bond Linkage" },
    { name: "license_id_fk", label: "License", type: "select", col: 3, options: licenseOptions },
    { name: "license_total_value", label: "License Total Value", type: "number", col: 2, readOnly: true },
    { name: "license_value", label: "License utilized value", type: "number", col: 2 },
    { name: "license_available_value", label: "License Available Value", type: "number", col: 2, readOnly: true },
    { name: "license_expiry_date", label: "License Expiry Date", type: "date", col: 3, readOnly: true },
    { name: "bond_id_fk", label: "Bond", type: "select", col: 3, options: bondOptions },
    { name: "bond_total_value", label: "Bond Total Value", type: "number", col: 2, readOnly: true },
    { name: "bond_value", label: "Bond utilized Value", type: "number", col: 2 },
    { name: "bond_available_value", label: "Bond Available Value", type: "number", col: 2, readOnly: true },
    { name: "bond_expiry_date", label: "Bond Expiry Date", type: "date", col: 3, readOnly: true },

    { name: "remarks", label: "Remarks", type: "textarea", col: 12 },
  ], [consigneeOptions, shipperOptions, linerOptions, CSFLocationOptions,
    bondOptions, licenseOptions, commodityOptions, indentingOptions, currencyOptions]);

  const initialFormState = useMemo(() => {
    const base = allFormFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), { job_id: 0 });
    // Default new jobs to USD unless/until the user picks a different currency.
    const usd = currencyOptions.find((o) => o.code === "USD");
    if (usd) base.currency_id_fk = usd.value;
    return base;
  }, [allFormFields, currencyOptions]);

  const [formData, setFormData] = useState(initialFormState);
  const [jobList, setJobsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // Fields actually rendered: hide HSS Party / Invoice No / Invoice Date
  // (via CommonModal's field.display support) unless "High Sea Sale" is checked
  const HSS_ONLY_FIELDS = ["hss_party", "hss_invoice_no", "hss_invoice_date"];
  const formFields = useMemo(
    () =>
      formData.hss
        ? allFormFields
        : allFormFields.map((f) =>
          HSS_ONLY_FIELDS.includes(f.name) ? { ...f, display: "none" } : f
        ),
    [allFormFields, formData.hss]
  );

  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
  }, []);

  /* ── Fetch helpers ── */
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/all_jobs/job/list");
      // Lot Completed (status 8) jobs move to the Job History module instead.
      if (response.status === 200) setJobsList(response.data.filter((j) => String(j.status) !== "8"));
      else showAlert("error", response.status, response.message);
    } catch (err) { showAlert("error", "Unexpected Error", err.message); }
  }, [showAlert]);

  const fetchConsigneeData = useCallback(async () => {
    try { const r = await post("/masters/consignees/list"); if (r.status === 200) setConsigneeOptions([{ value: "", label: "Select Consignee" }, ...r.data.map(i => ({ value: i.consignees_id, label: i.consignees_name })), { value: "others", label: "+ Add New Consignee" }]); } catch { }
  }, []);
  const fetchShipperData = useCallback(async () => {
    try { const r = await post("/masters/shipper/list"); if (r.status === 200) setShipperOptions([{ value: "", label: "Select Shipper" }, ...r.data.map(i => ({ value: i.shipper_id, label: i.long_name })), { value: "others", label: "+ Add New Shipper" }]); } catch { }
  }, []);
  const fetchLinerData = useCallback(async () => {
    try { const r = await post("/masters/liner/list"); if (r.status === 200) setLinerOptions([{ value: "", label: "Select Liner" }, ...r.data.map(i => ({ value: i.liner_id, label: i.code })), { value: "others", label: "+ Add New Liner" }]); } catch { }
  }, []);
  const fetchCFSLocationData = useCallback(async () => {
    try { const r = await post("/masters/Cfs_location/list"); if (r.status === 200) setCFSLocationOptions([{ value: "", label: "Select CFS Location" }, ...r.data.map(i => ({ value: i.cfs_location_id, label: i.cfs_code }))]); } catch { }
  }, []);
  const fetchBondData = useCallback(async () => {
    try { const r = await post("/compliance/bond/list"); if (r.status === 200) setBondOptions([{ value: "", label: "Select Bond" }, ...r.data.map(i => ({ value: i.bond_id, label: i.bond_number, total_value: i.bond_total_value, available_value: i.bond_avail_value, bond_expiry_date: i.bond_expiry_date || "" }))]); } catch { }
  }, []);
  const fetchLicenseData = useCallback(async () => {
    try { const r = await post("/compliance/license/list"); if (r.status === 200) setLicenseOptions([{ value: "", label: "Select License" }, ...r.data.map(i => ({ value: i.license_id, label: i.license_no, total_value: i.license_total_value, available_value: i.license_avail_value, license_expiry_date: i.expiry_date || "" }))]); } catch { }
  }, []);
  const fetchCommodityDate = useCallback(async () => {
    try { const r = await post("/masters/commodity/list"); if (r.status === 200) setCommodityOptions([{ value: "", label: "Select Commodity" }, ...r.data.map(i => ({ value: i.commodity_id, label: i.full_name })), { value: "others", label: "+ Add New Commodity" }]); } catch { }
  }, []);
  const fetchIndentingData = useCallback(async () => {
    try { const r = await post("/masters/indenting_agent/list"); if (r.status === 200) setIndentingOptions([{ value: "", label: "Select Indenting Agent" }, ...r.data.map(i => ({ value: i.indenting_agent_id, label: i.name }))]); } catch { }
  }, []);
  const fetchCurrencyData = useCallback(async () => {
    try { const r = await post("/masters/currency/list"); if (r.status === 200) setCurrencyOptions([{ value: "", label: "Select Currency" }, ...r.data.map(i => ({ value: i.currency_id, label: i.code, code: i.code }))]); } catch { }
  }, []);

  useEffect(() => {
    fetchData(); fetchConsigneeData(); fetchShipperData(); fetchLinerData();
    fetchCFSLocationData(); fetchLicenseData(); fetchBondData();
    fetchCommodityDate(); fetchIndentingData(); fetchCurrencyData();
  }, [fetchData, fetchConsigneeData, fetchShipperData, fetchLinerData,
    fetchCFSLocationData, fetchLicenseData, fetchBondData, fetchCommodityDate, fetchIndentingData, fetchCurrencyData]);


  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });

    // Prefer the fetched `packages` breakdown; fall back to the
    // legacy single no_of_packages/package_unit if that's all we have.
    if (Array.isArray(row.packages) && row.packages.length > 0) {
      setPackageLines(
        row.packages.map((p) => ({
          _key: `pl_${p.package_id ?? Math.random()}`,
          no_of_packages: p.no_of_packages ?? "",
          package_unit: p.package_unit ?? "",
        }))
      );
    } else if (row.no_of_packages || row.package_unit) {
      setPackageLines([{ ...emptyPackageLine(), no_of_packages: row.no_of_packages, package_unit: row.package_unit }]);
    } else {
      setPackageLines([emptyPackageLine()]);
    }

    setShowModal(true);
  }, [initialFormState]);

  const handleNewJob = useCallback(() => {
    setFormData(initialFormState);
    setPackageLines([emptyPackageLine()]);
    setShowModal(true);
  }, [initialFormState]);

  /* open document modal */
  const handleDocs = useCallback((row) => {
    setDocJob(row);
    setDocModal(true);
  }, []);

  const [containersModal, setContainersModal] = useState(false);
  const [containersJob, setContainersJob] = useState(null);

  const handleContainers = useCallback((row) => {
    setContainersJob(row);
    setContainersModal(true);
  }, []);

  /* ── "Others" → open the real master form (Consignee / Shipper / Liner / Commodity) ── */
  const [addConsigneeShow, setAddConsigneeShow] = useState(false);
  const [addShipperShow, setAddShipperShow] = useState(false);
  const [addLinerShow, setAddLinerShow] = useState(false);
  const [addCommodityShow, setAddCommodityShow] = useState(false);

  // Same field definitions as Consignees.js
  const consigneeFormFields = useMemo(() => [
    { name: "consignees_name", label: "Consignee Name", type: "text", required: true, col: 6 },
    { name: "code", label: "Short Code", type: "text", col: 6 },
    { name: "address", label: "Address", type: "textarea", col: 12 },
    { name: "city", label: "City", type: "text", col: 6 },
    { name: "state", label: "State", type: "text", col: 6 },
    { name: "pincode", label: "Pincode", type: "text", col: 6 },
    { name: "gst_in", label: "GSTIN", type: "text", col: 6 },
    { name: "pan", label: "PAN", type: "text", col: 6 },
    { name: "ie_code", label: "IE Code", type: "text", col: 6 },
    { name: "contact_person", label: "Contact Person", type: "text", col: 6 },
    { name: "phone", label: "Phone", type: "text", col: 6 },
    { name: "email", label: "Email", type: "email", col: 6 },
    {
      name: "status", label: "Status", type: "select", col: 6,
      options: [{ value: 0, label: "Active" }, { value: 1, label: "Inactive" }],
    },
  ], []);

  // Same field definitions as Shipper.js
  const shipperFormFields = useMemo(() => [
    { name: "long_name", label: "Long Name", type: "text", required: true, col: 6 },
    { name: "short_name", label: "Short Name", type: "text", col: 6 },
    { name: "address", label: "Unit-1(Primary address)", type: "text", col: 6 },
    { name: "address1", label: "Unit-2(Secondary address)", type: "text", col: 6 },
    {
      name: "status", label: "Status", type: "select", col: 6,
      options: [{ value: 0, label: "Active" }, { value: 1, label: "Inactive" }],
    },
  ], []);

  // Same field definitions as Liner.js
  const linerFormFields = useMemo(() => [
    { name: "liner_name", label: "Liner Name", type: "text", required: true, col: 6 },
    { name: "code", label: "Liner Code", type: "text", col: 6 },
    { name: "address", label: "address", type: "textarea", col: 12 },
    { name: "agent_name", label: "Agent Name", type: "text", col: 6 },
    { name: "agent_phone", label: "Agent Phone", type: "text", col: 6 },
    { name: "agent_email", label: "Agent Email", type: "email", col: 6 },
    {
      name: "status", label: "Status", type: "select", col: 6,
      options: [{ value: 0, label: "Active" }, { value: 1, label: "Inactive" }],
    },
  ], []);

  // Same field definitions as Commodity.js
  const commodityFormFields = useMemo(() => [
    { name: "full_name", label: "Full Name", type: "text", required: true, col: 6 },
    { name: "short_name", label: "Short Name", type: "text", col: 6 },
    { name: "address", label: "Address", type: "text", col: 6 },
    {
      name: "status", label: "Status", type: "select", col: 6,
      options: [{ value: 0, label: "Active" }, { value: 1, label: "Inactive" }],
    },
  ], []);

  const masterConfigs = useMemo(() => ({
    consignee: {
      title: "Consignee",
      endpoint: "/masters/consignees",
      listEndpoint: "/masters/consignees/list",
      idField: "consignees_id",
      hasPhoto: true,
      nameField: "consignees_name",
      valueKey: "consignees_id",
      labelKey: "consignees_name",
      targetField: "consignee_id_fk",
      placeholder: "Select Consignee",
      setOptions: setConsigneeOptions,
    },
    shipper: {
      title: "Shipper",
      endpoint: "/masters/shipper",
      listEndpoint: "/masters/shipper/list",
      idField: "shipper_id",
      hasPhoto: true,
      nameField: "long_name",
      valueKey: "shipper_id",
      labelKey: "long_name",
      targetField: "shipper_id_fk",
      placeholder: "Select Shipper",
      setOptions: setShipperOptions,
    },
    liner: {
      title: "Liner",
      endpoint: "/masters/liner",
      listEndpoint: "/masters/liner/list",
      idField: "liner_id",
      hasPhoto: false,
      nameField: "liner_name",
      valueKey: "liner_id",
      labelKey: "code",
      targetField: "liner_id_fk",
      placeholder: "Select Liner",
      setOptions: setLinerOptions,
    },
    commodity: {
      title: "Commodity",
      endpoint: "/masters/commodity",
      listEndpoint: "/masters/commodity/list",
      idField: "commodity_id",
      hasPhoto: true,
      nameField: "full_name",
      valueKey: "commodity_id",
      labelKey: "full_name",
      targetField: "cargo_desc",
      placeholder: "Select Commodity",
      setOptions: setCommodityOptions,
    },
  }), []);

  // Refresh the master's dropdown list and auto-select the newly created record in the job form
  const refreshMasterAndSelect = useCallback(async (config, savedData) => {
    try {
      const listRes = await post(config.listEndpoint);
      if (listRes.status === 200) {
        const opts = [
          { value: "", label: config.placeholder },
          ...listRes.data.map((i) => ({ value: i[config.valueKey], label: i[config.labelKey] })),
          { value: "others", label: `+ Add New ${config.title}` },
        ];
        config.setOptions(opts);

        const match = listRes.data.find(
          (item) =>
            String(item[config.nameField] || "").trim().toLowerCase() ===
            String(savedData[config.nameField] || "").trim().toLowerCase()
        );
        if (match) {
          setFormData((prev) => ({ ...prev, [config.targetField]: match[config.valueKey] }));
        }
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  /* ── Table columns ── */
  const columns = useMemo(() => {
    // Static columns
    const staticColumns = [
      {
        header: "S.No",
        label_condition: "s_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-2 align-items-center">
            <i
              className="ri-file-edit-fill text-primary cursor-pointer"
              onClick={() => handleEdit(row)}
              title="Edit Job"
              style={{ fontSize: 18 }}
            />
            <i
              className="ri-folder-open-fill text-warning cursor-pointer"
              onClick={() => handleDocs(row)}
              title="Documents"
              style={{ fontSize: 18 }}
            />
            <i
              className="ri-building-4-fill text-success cursor-pointer"
              onClick={() => handleContainers(row)}
              title="Containers"
              style={{ fontSize: 18 }}
            />
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ];

    // Dynamic columns from jobList keys
    const dynamicColumns =
      jobList?.length > 0
        ? Object.keys(jobList[0])
          .filter(
            (key) =>
              ![
                "job_id",
                "doc_count",
                "packages",
                "currency_code",
                "currency_symbol",
                "shipper_name",
                "indenting_agent_name",
              ].includes(key) &&
              !key.endsWith("_id") &&
              !key.endsWith("_id_fk")
          )
          .map((key) => ({
            header: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()),

            accessorKey: key,

            ...(key === "status" && {
              label_condition: "job_status",
            }),

            // Invoice Value shown with its currency symbol, e.g. "$ 41787.90"
            ...(key === "invoice_value" && {
              cell: ({ row }) =>
                row.invoice_value
                  ? `${row.currency_symbol || ""} ${row.invoice_value}`.trim()
                  : row.invoice_value,
            }),
          }))
        : [];

    // Packages column — shows every package type, e.g. "20 Bales, 15 Bags"
    const packagesColumn = {
      header: "Packages",
      cell: ({ row }) => (
        <span title={summarizePackages(row.packages)}>
          {summarizePackages(row.packages)}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    };

    // Docs column
    const docsColumn = {
      header: "Docs",
      cell: ({ row }) => {
        const count = row.doc_count || 0;

        return (
          <span
            className={`badge ${count > 0 ? "bg-warning text-dark" : "bg-secondary"
              }`}
            style={{ cursor: "pointer", fontSize: 11 }}
            onClick={() => handleDocs(row)}
            title="View documents"
          >
            <i className="ri-file-list-line me-1" />
            {count}
          </span>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
    };

    return [...staticColumns, ...dynamicColumns, packagesColumn, docsColumn];
  }, [jobList, handleEdit, handleDocs]);

  /* ── Input change ── */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // ─── "Others" selected → open the real master add form ────────
    if (value === "others") {
      if (name === "consignee_id_fk") { setAddConsigneeShow(true); return; }
      if (name === "shipper_id_fk") { setAddShipperShow(true); return; }
      if (name === "liner_id_fk") { setAddLinerShow(true); return; }
      if (name === "cargo_desc") { setAddCommodityShow(true); return; }
    }

    let updated = { ...formData, [name]: value };

    // ─── High Sea Sale toggle ──────────────────────────────────────
    if (name === "hss" && !value) {
      updated.hss_party = "";
      updated.hss_invoice_no = "";
      updated.hss_invoice_date = "";
    }

    // ─── License selection ───────────────────────────────────────
    if (name === "license_id_fk") {
      const sel = licenseOptions.find((l) => String(l.value) === String(value));
      if (sel) {
        // Check expiry first
        if (sel.license_expiry_date && new Date(sel.license_expiry_date) < new Date()) {
          showAlert("warning", "License Expired", "Selected license is expired!");
          updated = {
            ...updated,
            license_id_fk: "",
            license_total_value: "",
            license_available_value: "",
            license_expiry_date: "",
          };
        } else {
          updated = {
            ...updated,
            license_total_value: sel.total_value || "",
            license_available_value: sel.total_value || "",
            license_expiry_date: sel.license_expiry_date || "",
            // Reset utilized value when license changes
            license_value: "",
          };
        }
      } else {
        updated = {
          ...updated,
          license_total_value: "",
          license_available_value: "",
          license_expiry_date: "",
          license_value: "",
        };
      }
    }

    // ─── Bond selection ──────────────────────────────────────────
    if (name === "bond_id_fk") {
      const sel = bondOptions.find((l) => String(l.value) === String(value));
      if (sel) {
        if (sel.bond_expiry_date && new Date(sel.bond_expiry_date) < new Date()) {
          showAlert("warning", "Bond Expired", "Selected bond is expired!");
          updated = {
            ...updated,
            bond_id_fk: "",
            bond_total_value: "",
            bond_available_value: "",
            bond_expiry_date: "",
          };
        } else {
          updated = {
            ...updated,
            bond_total_value: sel.total_value || "",
            bond_available_value: sel.available_value || "",
            bond_expiry_date: sel.bond_expiry_date || "",
            // Reset utilized value when bond changes
            bond_value: "",
          };
        }
      } else {
        updated = {
          ...updated,
          bond_total_value: "",
          bond_available_value: "",
          bond_expiry_date: "",
          bond_value: "",
        };
      }
    }

    // ─── License utilized value ──────────────────────────────────
    if (name === "license_value") {
      const utilized = parseFloat(value) || 0;
      // ✅ Read from formData (original DB available), not updated (already mutated)
      const totalvalue = parseFloat(formData.license_total_value) || 0;
      if (utilized < 0) {
        showAlert("warning", "Invalid", "Value cannot be negative.");
        updated.license_value = "";
        updated.license_available_value = totalvalue;
      } else if (utilized > totalvalue) {
        showAlert("warning", "Exceeds Limit", `License utilized value cannot exceed available value (${totalvalue}).`);
        updated.license_value = "";
        updated.license_available_value = totalvalue;
      } else {
        updated.license_available_value = +(parseFloat(totalvalue) - parseFloat(utilized)).toFixed(2);
      }
    }

    // ─── Bond utilized value ─────────────────────────────────────
    if (name === "bond_value") {
      const utilized = parseFloat(value) || 0;
      // ✅ Read from formData (original DB available), not updated
      const bond_total_value = parseFloat(formData.bond_total_value) || 0;

      if (utilized < 0) {
        showAlert("warning", "Invalid", "Value cannot be negative.");
        updated.bond_value = "";
        updated.bond_available_value = bond_total_value;
      } else if (utilized > bond_total_value) {
        showAlert("warning", "Exceeds Limit", `Bond utilized value cannot exceed available value (${bond_total_value}).`);
        updated.bond_value = "";
        updated.bond_available_value = bond_total_value;
      } else {
        updated.bond_available_value = +(bond_total_value - utilized).toFixed(2);
      }
    }

    // ─── CFS Free Date auto-calculate (CFS Free Days + POD Arrival) ──
    if (name === "free_days" || name === "pod_arrival") {
      const podDate = name === "pod_arrival" ? value : updated.pod_arrival;
      const freeDays = parseInt(name === "free_days" ? value : updated.free_days, 10);
      if (podDate && !isNaN(freeDays) && freeDays >= 0) {
        const d = new Date(podDate);
        d.setDate(d.getDate() + freeDays - 1);
        updated.free_date = d.toISOString().split("T")[0];
      } else {
        updated.free_date = "";
      }
    }

    // ─── Detention Free Date auto-calculate (Detention Free Days + POD Arrival) ──
    if (name === "dem_upto" || name === "pod_arrival") {
      const podDate = name === "pod_arrival" ? value : updated.pod_arrival;
      const detentionDays = parseInt(name === "dem_upto" ? value : updated.dem_upto, 10);
      if (podDate && !isNaN(detentionDays) && detentionDays >= 0) {
        const d = new Date(podDate);
        d.setDate(d.getDate() + detentionDays - 1);
        updated.detention_upto = d.toISOString().split("T")[0];
      } else {
        updated.detention_upto = "";
      }
    }

    // ─── Demurrage Free Date auto-calculate (Demurrage Free Days + POD Arrival) ──
    if (name === "demurrage_free_days" || name === "pod_arrival") {
      const podDate = name === "pod_arrival" ? value : updated.pod_arrival;
      const demurrageDays = parseInt(name === "demurrage_free_days" ? value : updated.demurrage_free_days, 10);
      if (podDate && !isNaN(demurrageDays) && demurrageDays >= 0) {
        const d = new Date(podDate);
        d.setDate(d.getDate() + demurrageDays - 1);
        updated.demurrage_free_date = d.toISOString().split("T")[0];
      } else {
        updated.demurrage_free_date = "";
      }
    }

    setFormData(updated);
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
    setPackageLines([emptyPackageLine()]);
  };

  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) { showAlert("error", "Oops...", `${field.label} is required`); return; }
    }


    try {
      const payload = {
        ...formData
      };
      const response = await post("/all_jobs/job", payload);
      if (response.status === 200) { showAlert("success", "Saved", response.message); fetchData(); handleClose(); }
      else showAlert("error", response.status, response.message);
    } catch (err) { showAlert("error", "Unexpected Error", err.message); }
  };

  /* ── Job modal header: BL No. / Job No. / Consignee ── */
  const selectedConsigneeName = useMemo(() => {
    const sel = consigneeOptions.find((c) => String(c.value) === String(formData.consignee_id_fk));
    return sel && sel.value ? sel.label : "";
  }, [consigneeOptions, formData.consignee_id_fk]);

  const jobModalTitle = (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        margin: "-1rem -1rem 0.75rem -1rem",
        padding: "10px 16px",
        color: "#fff",
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
      }}
    >
      <div className="fw-semibold" style={{ fontSize: 15 }}>
        <i className="ri-file-list-3-fill me-2" />
        {formData.job_id ? "Edit Job" : "New Job"}
      </div>
      <div className="d-flex flex-wrap gap-2 mt-1">
        <span
          className="badge d-inline-flex align-items-center"
          style={{ background: "rgba(255,255,255,0.18)", fontWeight: 500, fontSize: 11.5 }}
        >
          <i className="ri-file-text-line me-1" />
          BL No:&nbsp;<strong>{formData.bl_number || "—"}</strong>
        </span>
        <span
          className="badge d-inline-flex align-items-center"
          style={{ background: "rgba(255,255,255,0.18)", fontWeight: 500, fontSize: 11.5 }}
        >
          <i className="ri-hashtag me-1" />
          Job No:&nbsp;<strong>{formData.job_no || "New"}</strong>
        </span>
        <span
          className="badge d-inline-flex align-items-center"
          style={{ background: "rgba(255,255,255,0.18)", fontWeight: 500, fontSize: 11.5 }}
        >
          <i className="ri-user-3-line me-1" />
          Consignee:&nbsp;<strong>{selectedConsigneeName || "—"}</strong>
        </span>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
          .modal-dialog.modal-dialog-scrollable .modal-content,
          .modal-content {
            max-height: 90vh;
          }
          .modal-content .modal-header {
            position: sticky;
            top: 0;
            z-index: 1055;
            flex-shrink: 0;
          }
          .modal-content .modal-footer {
            position: sticky;
            bottom: 0;
            z-index: 1055;
            flex-shrink: 0;
          }
          .modal-content .modal-body {
            overflow-y: auto;
            flex: 1 1 auto;
          }
        `}</style>

      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">All Jobs</div>
          <div className="page-header-sub">{jobList?.length} Total Jobs</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleNewJob}>
          New Jobs
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">All Jobs List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="All_jobs List"
            columns={columns}
            data={jobList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      {/* Job form modal */}
      <JobFormModal
        show={showModal}
        titleContent={jobModalTitle}
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />

      {/* Document modal */}
      <JobDocumentModal
        show={docModal}
        job={docJob}
        onClose={() => { setDocModal(false); setDocJob(null); }}
      />
      <JobContainersModal
        show={containersModal}
        job={containersJob}
        onClose={() => { setContainersModal(false); setContainersJob(null); }}
      />

      <MasterFormModal
        show={addConsigneeShow}
        title="Consignee"
        formFields={consigneeFormFields}
        idField={masterConfigs.consignee.idField}
        endpoint={masterConfigs.consignee.endpoint}
        hasPhoto={masterConfigs.consignee.hasPhoto}
        onClose={() => setAddConsigneeShow(false)}
        onSaved={(data) => refreshMasterAndSelect(masterConfigs.consignee, data)}
      />
      <MasterFormModal
        show={addShipperShow}
        title="Shipper"
        formFields={shipperFormFields}
        idField={masterConfigs.shipper.idField}
        endpoint={masterConfigs.shipper.endpoint}
        hasPhoto={masterConfigs.shipper.hasPhoto}
        onClose={() => setAddShipperShow(false)}
        onSaved={(data) => refreshMasterAndSelect(masterConfigs.shipper, data)}
      />
      <MasterFormModal
        show={addLinerShow}
        title="Liner"
        formFields={linerFormFields}
        idField={masterConfigs.liner.idField}
        endpoint={masterConfigs.liner.endpoint}
        hasPhoto={masterConfigs.liner.hasPhoto}
        onClose={() => setAddLinerShow(false)}
        onSaved={(data) => refreshMasterAndSelect(masterConfigs.liner, data)}
      />
      <MasterFormModal
        show={addCommodityShow}
        title="Commodity"
        formFields={commodityFormFields}
        idField={masterConfigs.commodity.idField}
        endpoint={masterConfigs.commodity.endpoint}
        hasPhoto={masterConfigs.commodity.hasPhoto}
        onClose={() => setAddCommodityShow(false)}
        onSaved={(data) => refreshMasterAndSelect(masterConfigs.commodity, data)}
      />
    </>
  );
};

export default All_jobs;
